/**
 * 이상한 방 - 모바일 우선 조작 레이어
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 원칙 2(1절) · 3.1절 · 4.3절 · 6.1절 · 9.4절
 *
 * ## 이 파일이 존재하는 이유는 미관이 아니다
 *
 * 4주 계획의 진짜 병목은 코딩이 아니라 **"6명 모으기"**다(9.4절). 링크를 던져 폰에서 바로
 * 들어올 수 있으면 플레이테스트 횟수가 달라지고, 그 한 줄이 아이소메트릭 전환의 근거였다.
 * 그런데 기존 HUD는 전부 마우스 기준(hover · 12px 글자 · 4px 여백)이라 **폰에서 못 누른다.**
 * 이 파일은 그 구멍을 메우는 최소 조작 세트다.
 *
 * ## 여기 있는 버튼은 전부 "없으면 게임이 성립하지 않는" 것뿐이다
 *
 *  1. **시점 회전(좌/우 90°)** - 선택 기능이 아니다. 아이소메트릭의 고정 시점은 한 각도에서
 *     영원히 가려지는 물건을 만들고, 그건 난이도가 아니라 **찾을 수 없는 정답**이다(원칙 2).
 *     제스처(두 손가락 트위스트)만으로 두면 그 기능을 아무도 발견하지 못한다.
 *  2. **줌 ±** - 줌이 1인칭의 시야 차단을 대체하므로 줌이 곧 게임 루프다. 핀치는 한 손으로
 *     안 되고 손가락이 화면을 가린다. 버튼이 대안이 아니라 기본에 가깝다.
 *  3. **모드 전환** - 탭의 의미(지목/배치)를 바꾸는 유일한 스위치. 폰에는 Tab 키가 없다.
 *  4. **배치 확인 바** - 손가락에는 hover가 없어 "놓기 전에 보기"가 성립하지 않는다.
 *     지정과 확정을 가르는 2단계의 두 번째 단계가 이 바다(`editor.ts` 상단 참고).
 *
 * ## 상태를 갖지 않는다
 *
 * HUD와 같은 규약이다 - **표시와 클릭 전달만** 한다. 줌 값도 모드도 여기서 기억하지 않고
 * 매 `render(view)`마다 통째로 받는다. 조작 레이어가 자기 상태를 갖는 순간
 * "버튼은 배치인데 실제로는 관찰"이 생기고, 그 불일치는 오조작으로 직결된다.
 *
 * ## 화면 한복판을 비웠다 (2026-09-01 변경)
 *
 * 회전·줌 묶음은 화면 오른쪽 **한복판**에 떠 있었다. 근거는 "엄지가 가장 안정적으로 닿는 높이"
 * 였는데, 이 게임에서는 그 근거보다 앞서는 것이 있다 - UI가 덮은 면적은 곧 **볼 수 없는 방의
 * 넓이**이고, 변화를 찾는 게임에서 그건 미관이 아니라 공정성이다(원칙 2).
 * 이제 우하단 모서리에 붙고, **만지지 않는 동안에는 흐려진다**(`.is-awake`).
 *
 * 흐리게 하는 일을 CSS `:hover`에 맡길 수 없어서 이 파일이 관여한다. 손가락에는 hover가 없고,
 * 터치에서 "지금 쓰는 중"을 아는 방법은 실제로 눌렀다는 사실뿐이다.
 *
 * ## 문구는 이 파일이 정하지 않는다
 *
 * 라벨과 `aria-label`은 전부 `i18n`의 `t()`를 거친다. 여기 한국어를 박아 두면 영어 사전과
 * 갈라지고, 특히 `aria-label`은 눈으로 확인되지 않아 갈라진 것을 아무도 못 본다.
 *
 * 스타일은 `hud.css`가 소유한다(HUD와 같은 팔레트·같은 안전영역 규칙을 써야 하므로).
 */

import { onLocaleChange, t, type MessageKey } from '../i18n'

import { createElement, createRepaintRegistry, restartAnimation, setDisabled, setText, type Repaint } from './dom'
import { publishSize } from './overlayLayout'

import './hud.css'

/* --- 튜닝 값 --------------------------------------------------------------------- */

/**
 * 줌 버튼 한 번의 정규화 줌 증감.
 * 정규화 축이 기하급수이므로(`isoCamera.ts`) 이 값은 "누를 때마다 몇 배"에 해당한다 -
 * 0.12면 전 구간을 약 여덟 번에 지난다. 더 크면 훑다가 목표를 지나치고,
 * 더 작으면 소품 하나를 크게 보기까지 열 번 넘게 눌러야 한다.
 */
const ZOOM_BUTTON_STEP = 0.12

/** 줌 슬라이더의 눈금. 100단계면 손가락으로 끌 때 값이 튀지 않는다. */
const ZOOM_SLIDER_STEP = 0.01
/** 위 눈금을 문자열로 적을 때 필요한 소수 자릿수. 둘이 갈리면 슬라이더가 매 프레임 다시 대입된다. */
const ZOOM_SLIDER_DECIMALS = 2

/** 탭 표식이 화면에 남는 시간(ms). 히트마커(`gameHud.ts`)와 같은 근거 - 겹치면 재시작되므로 항상 마지막 한 번의 표식만 보인다. */
const TAP_FLASH_MS = 420

/** 4방향 회전 인덱스의 개수. 나침반 점의 수와 같다. */
const DIRECTION_COUNT = 4

/**
 * 만진 뒤 조작 묶음이 또렷하게 남아 있는 시간(ms).
 *
 * 짧으면 줌 버튼을 연달아 누르는 사이에 흐려져 깜빡이는 것처럼 보이고, 길면 흐려지는 의미가
 * 없어진다. 2.4초는 "한 번 더 누를까"를 판단하는 사이만 남는 길이다.
 */
const AWAKE_MS = 2400

/* --- 공개 타입 ------------------------------------------------------------------- */

/**
 * 4방향 회전 인덱스.
 *
 * `input/isoCamera.ts`가 같은 이름의 타입을 export 하지만 여기서 다시 선언한다.
 * 이유는 의존 방향이다 - UI가 입력 레이어를 import 하면 HUD 테스트가 카메라를 끌고 온다.
 * 둘 다 `0 | 1 | 2 | 3`이라 구조적으로 동일하므로 그대로 주고받을 수 있다
 * (`render/cutaway.ts`가 같은 이유로 같은 선택을 했다).
 */
export type IsoDirection = 0 | 1 | 2 | 3

/** 앱의 두 모드. `ui/hud.ts`의 `HudMode`와 같은 값이며 같은 이유로 구조적으로만 맞춘다. */
export type ControlMode = 'observe' | 'place'

/**
 * 사격 결과의 화면 표현. 값은 `game/hunt.ts`의 `ShotResultLike['kind']`와 일부러 같다
 * (`dry`만 화면 쪽에서 더한다) - 번역 계층을 만들지 않는다.
 */
export type ShotFeedback =
  | 'object'
  | 'removed'
  | 'miss'
  /** 탄환이 없는 상태에서의 격발. 4.3절: 사격 불가 → 관전. 헛방아쇠라는 사실 자체를 알려야 한다. */
  | 'dry'

/**
 * 확인 바가 그리는 데 필요한 전부.
 * `input/editor.ts`의 `EditorAim`이 그대로 대입되는 부분집합이다 - 그쪽이 진실이고 여기는 표시다.
 */
export type AimStatus = Readonly<{
  action: 'add' | 'move' | 'none'
  /** 지금 확정하면 `verbs.ts`가 승인하는가. 아니면 확인 버튼을 **누를 수 없게** 만든다(4.3절). */
  canCommit: boolean
  subjectName: string | null
  /** 완전 대칭이라 회전 동사가 잠겼는가(4.2절). */
  isRotateLocked: boolean
}>

export type MobileControlsView = Readonly<{
  mode: ControlMode
  /** 정규화 줌(0 = 전체 조망, 1 = 최대 확대). */
  zoom: number
  direction: IsoDirection
  aim: AimStatus
}>

/** 이 레이어가 바깥으로 내보내는 유일한 출력. 전부 "사용자가 무엇을 눌렀다"뿐이다. */
export type MobileControlHandlers = Readonly<{
  onRotateView: (delta: 1 | -1) => void
  /** 줌 버튼. 인자는 정규화 줌의 증감분이며 클램프는 카메라가 한다. */
  onZoomStep: (delta: number) => void
  /** 줌 슬라이더. 인자는 정규화 줌의 절댓값. */
  onZoomTo: (zoom: number) => void
  onToggleMode: () => void
  /** 지정한 자리에 확정(추가 또는 이동). */
  onConfirm: () => void
  onCancel: () => void
  /** 배치 대상 90° 회전(키보드 R과 같은 경로). */
  onRotateSubject: () => void
  onRemove: () => void
}>

export type MobileControlsHandle = Readonly<{
  render: (view: MobileControlsView) => void
  /**
   * 탭한 자리에 결과 표식을 띄운다(클라이언트 좌표).
   *
   * 아이소메트릭에는 크로스헤어가 없다 - 조준점이 화면 중앙이 아니라 **손가락이 닿은 곳**이다.
   * 표식이 없으면 "눌렀는데 반응이 없다"와 "빗나갔다"가 구별되지 않고,
   * 탄환이 하나 사라진 이유를 화면 어디에서도 찾을 수 없게 된다(4.3절).
   */
  flashTap: (clientX: number, clientY: number, feedback: ShotFeedback) => void
  dispose: () => void
}>

export type MobileControlsOptions = Readonly<{
  mount: HTMLElement
  handlers: MobileControlHandlers
}>

/* --- DOM 조립 헬퍼 --------------------------------------------------------------- */

/**
 * 조작 버튼 하나.
 *
 * `aria-label`을 반드시 붙인다 - 라벨이 기호(↺ · +)뿐이라 스크린리더에는 아무 의미가 없고,
 * 기호는 폰트에 따라 통째로 빠지기도 한다. 크기(44×44 이상)는 `hud.css`가 강제한다.
 * 기호는 번역하지 않고 이름만 번역한다 - `+`는 어느 언어에서나 `+`다.
 */
function createButton(
  className: string,
  glyph: string,
  label: MessageKey,
  repaint: Repaint,
): HTMLButtonElement {
  const button = createElement('button', className)
  button.type = 'button'
  repaint(() => button.setAttribute('aria-label', t(label)))
  button.append(createElement('span', 'mc-btn__glyph', glyph))
  return button
}

/**
 * "지금 만지고 있다"를 클래스 하나로 알린다.
 *
 * 조작 묶음은 관찰 중에 흐려져 있다가(`hud.css`의 `--hud-idle`) 만지면 또렷해진다.
 * 이 판단을 CSS `:hover`에 맡길 수 없는 이유는 손가락에 hover가 없기 때문이고,
 * 그래서 실제로 눌렀다는 사실만이 유일한 신호다.
 *
 * `pointermove`까지 듣는 것은 슬라이더 때문이다 - 길게 끄는 동안 `pointerdown`은 한 번뿐이라
 * 끄는 도중에 흐려진다. 마우스 hover는 CSS가 이미 처리하므로 여기서 중복돼도 결과는 같다.
 */
function createWaker(element: HTMLElement): { stop: () => void } {
  let timer: number | null = null

  function wake(): void {
    if (timer !== null) clearTimeout(timer)
    element.classList.add('is-awake')
    timer = window.setTimeout(() => {
      element.classList.remove('is-awake')
      timer = null
    }, AWAKE_MS)
  }

  element.addEventListener('pointerdown', wake)
  element.addEventListener('pointermove', wake)

  return {
    stop(): void {
      if (timer !== null) clearTimeout(timer)
      timer = null
    },
  }
}

/** 지금 보고 있는 방향을 점 네 개로 알린다. 회전이 있어도 **어디서 보는지**를 모르면 없는 것과 같다. */
function createCompass(): { root: HTMLElement; dots: readonly HTMLElement[] } {
  const root = createElement('div', 'mc-compass')
  root.setAttribute('aria-hidden', 'true')

  const dots: HTMLElement[] = []
  for (let index = 0; index < DIRECTION_COUNT; index += 1) {
    const dot = createElement('span', 'mc-compass__dot')
    root.append(dot)
    dots.push(dot)
  }

  return { root, dots }
}

/* --- 조립 ----------------------------------------------------------------------- */

/** 조작 레이어를 만들어 `mount`에 붙인다. 정리는 반환된 핸들의 `dispose()`로만 한다. */
export function createMobileControls(options: MobileControlsOptions): MobileControlsHandle {
  const { handlers } = options

  const root = createElement('div', 'mc-root')

  const { repaint, repaintAll } = createRepaintRegistry()

  /* 우하단 조작 묶음: 시점 회전 · 줌 */
  const cluster = createElement('div', 'mc-cluster')

  const rotateLeft = createButton('mc-btn', '↺', 'editor.viewLeft', repaint)
  const rotateRight = createButton('mc-btn', '↻', 'editor.viewRight', repaint)
  const zoomIn = createButton('mc-btn', '+', 'editor.zoomIn', repaint)
  const zoomOut = createButton('mc-btn', '−', 'editor.zoomOut', repaint)

  const compass = createCompass()

  // 슬라이더는 버튼의 보조다. 미세 조정과 "지금 얼마나 당겨져 있는가"의 표시를 겸한다 -
  // 버튼만 두면 전체 조망으로 돌아오는 데 여덟 번을 눌러야 한다.
  const zoomSlider = createElement('input', 'mc-zoom')
  zoomSlider.type = 'range'
  zoomSlider.min = '0'
  zoomSlider.max = '1'
  zoomSlider.step = String(ZOOM_SLIDER_STEP)
  zoomSlider.value = '0'
  repaint(() => zoomSlider.setAttribute('aria-label', t('editor.zoom')))

  /*
   * 나침반·버튼·슬라이더를 한 묶음에 넣는다. 따로 띄우면 화면 크기마다 셋의 간격을
   * 각각 맞춰야 하고, 좁은 화면에서 반드시 하나가 다른 것 위로 올라탄다.
   * 묶여 있어야 "만지면 또렷해진다"도 한 번에 걸린다 - 흐림이 조각나면 깜빡이는 것처럼 보인다.
   */
  const grid = createElement('div', 'mc-cluster__grid')
  grid.append(rotateLeft, rotateRight, zoomIn, zoomOut)
  cluster.append(compass.root, grid, zoomSlider)
  root.append(cluster)

  /* 좌상단: 모드 전환 - 탭의 의미를 바꾸는 유일한 스위치라 단독으로 크게 둔다 */
  const modeToggle = createElement('button', 'mc-mode')
  modeToggle.type = 'button'
  const modeToggleLabel = createElement('span', 'mc-mode__label')
  modeToggle.append(modeToggleLabel)
  root.append(modeToggle)

  /* 하단: 배치 확인 바(2단계의 두 번째 단계) */
  const aimBar = createElement('div', 'mc-aim')
  const aimName = createElement('span', 'mc-aim__name')
  const aimRotate = createButton('mc-aim__btn', '⟳', 'editor.rotateSelected', repaint)
  const aimRemove = createButton('mc-aim__btn', '✕', 'editor.removeSelected', repaint)
  const aimCancel = createElement('button', 'mc-aim__btn mc-aim__btn--text')
  aimCancel.type = 'button'
  const aimCancelLabel = createElement('span', 'mc-btn__glyph')
  repaint(() => setText(aimCancelLabel, t('common.cancel')))
  aimCancel.append(aimCancelLabel)
  const aimConfirm = createElement('button', 'mc-aim__confirm')
  aimConfirm.type = 'button'
  const aimConfirmLabel = createElement('span', 'mc-aim__confirm-label')
  aimConfirm.append(aimConfirmLabel)

  aimBar.append(aimName, aimRotate, aimRemove, aimCancel, aimConfirm)
  root.append(aimBar)

  /*
   * 관찰 중에는 조작 묶음도 모드 전환도 흐려져 있다가 만지면 또렷해진다.
   * 둘을 따로 깨우는 이유는 서로 화면 반대편에 있기 때문이다 - 우하단을 만졌다고
   * 좌상단이 같이 밝아지면 손이 안 간 쪽에서 깜빡임만 보인다.
   */
  const clusterWaker = createWaker(cluster)
  const modeWaker = createWaker(modeToggle)

  /* 탭 표식. 매번 만들지 않고 하나를 재사용한다 - 연타할 때 DOM이 쌓이면 그 자체가 프레임을 먹는다. */
  const tapFlash = createElement('div', 'mc-tap')
  tapFlash.setAttribute('aria-hidden', 'true')
  root.append(tapFlash)

  options.mount.append(root)

  /*
   * 조작 묶음의 높이를 내보낸다. 아래 오른쪽 기둥의 **맨 위**가 이것이라, 위 기둥(`.ol-stack`)이
   * 어디까지 자랄 수 있는지가 이 값으로 정해진다. 폰 세로에서 구획 진행판이 이 묶음 위로 내려앉아
   * 3,390px² 겹쳤던 자리다 - 위와 아래가 서로의 높이를 모르면 좁은 화면에서 반드시 만난다.
   */
  const clusterSize = publishSize(cluster, { height: '--mc-height' })

  let flashTimer: number | null = null

  /* --- 리스너 ------------------------------------------------------------------ */

  rotateLeft.addEventListener('click', () => handlers.onRotateView(-1))
  rotateRight.addEventListener('click', () => handlers.onRotateView(1))
  zoomIn.addEventListener('click', () => handlers.onZoomStep(ZOOM_BUTTON_STEP))
  zoomOut.addEventListener('click', () => handlers.onZoomStep(-ZOOM_BUTTON_STEP))
  modeToggle.addEventListener('click', () => handlers.onToggleMode())

  aimRotate.addEventListener('click', () => handlers.onRotateSubject())
  aimRemove.addEventListener('click', () => handlers.onRemove())
  aimCancel.addEventListener('click', () => handlers.onCancel())
  aimConfirm.addEventListener('click', () => handlers.onConfirm())

  // `input`이지 `change`가 아니다. `change`만 들으면 손가락을 뗄 때까지 화면이 따라오지 않아
  // 슬라이더가 "먹통"으로 느껴진다.
  zoomSlider.addEventListener('input', () => {
    const value = Number.parseFloat(zoomSlider.value)
    if (Number.isFinite(value)) handlers.onZoomTo(value)
  })

  /*
   * 마지막으로 받은 뷰.
   *
   * "상태를 갖지 않는다"는 이 파일의 계약을 깨는 것처럼 보이지만 그렇지 않다. 금지되는 것은
   * **모드나 줌 같은 진실**을 여기서 기억하는 것이고("버튼은 배치인데 실제로는 관찰"이 그 실패다),
   * 여기 담기는 것은 마지막 렌더의 입력이다. 쓰이는 곳도 하나뿐이다 - 언어가 바뀌었을 때
   * 같은 값을 새 언어로 다시 그리는 것. 다음 `render`가 오면 그대로 덮인다.
   */
  let lastView: MobileControlsView | null = null

  function draw(view: MobileControlsView): void {
    const isPlacing = view.mode === 'place'

    root.classList.toggle('is-place', isPlacing)
    // 라벨은 **바뀔 대상**을 말한다(누르면 저게 된다). 그래서 지금 모드의 반대말이다.
    setText(modeToggleLabel, t(isPlacing ? 'editor.modeLook' : 'editor.modePlace'))
    // 값이 같아도 setAttribute는 속성 변경으로 잡히므로 바뀔 때만 쓴다.
    // 로케일이 바뀌면 `draw(lastView)`가 다시 불리므로 비교 방식이어도 번역은 갱신된다.
    const modeToggleName = t(isPlacing ? 'editor.toLookMode' : 'editor.toPlaceMode')
    if (modeToggle.getAttribute('aria-label') !== modeToggleName) {
      modeToggle.setAttribute('aria-label', modeToggleName)
    }

    // 슬라이더는 사용자가 끄는 중에도 render가 돌므로 **눈금 하나보다 크게 어긋날 때만** 덮어쓴다.
    // 문자열 비교로는 "0.3"과 "0.30"이 달라져 매 프레임 대입이 일어나고, 그러면 드래그 중
    // 손잡이가 계속 제자리로 튄다. 비교는 반드시 수치로 한다.
    const shown = Number.parseFloat(zoomSlider.value)
    if (!Number.isFinite(shown) || Math.abs(shown - view.zoom) > ZOOM_SLIDER_STEP / 2) {
      zoomSlider.value = view.zoom.toFixed(ZOOM_SLIDER_DECIMALS)
    }

    compass.dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === view.direction)
    })

    aimBar.classList.toggle('is-hidden', !isPlacing)
    setText(aimName, view.aim.subjectName ?? t('editor.pickPrompt'))
    setText(aimConfirmLabel, t(view.aim.action === 'move' ? 'editor.confirmMove' : 'editor.confirmPlace'))

    setDisabled(aimConfirm, !view.aim.canCommit)
    setDisabled(aimRotate, view.aim.subjectName === null || view.aim.isRotateLocked)
    setDisabled(aimRemove, view.aim.action !== 'move')
  }

  /* 언어가 바뀌면 이 레이어가 스스로 다시 그린다(사전 엔진은 DOM을 건드리지 않는다). */
  const stopLocaleWatch = onLocaleChange(() => {
    repaintAll()
    if (lastView !== null) draw(lastView)
  })

  return {
    render(view: MobileControlsView): void {
      lastView = view
      draw(view)
    },

    flashTap(clientX: number, clientY: number, feedback: ShotFeedback): void {
      if (flashTimer !== null) clearTimeout(flashTimer)

      tapFlash.classList.remove('is-visible')
      restartAnimation(tapFlash)

      tapFlash.style.left = `${clientX}px`
      tapFlash.style.top = `${clientY}px`
      tapFlash.dataset['shot'] = feedback
      tapFlash.classList.add('is-visible')

      flashTimer = window.setTimeout(() => {
        tapFlash.classList.remove('is-visible')
        flashTimer = null
      }, TAP_FLASH_MS)
    },

    dispose(): void {
      if (flashTimer !== null) clearTimeout(flashTimer)
      flashTimer = null
      clusterWaker.stop()
      modeWaker.stop()
      // 구독을 끊지 않으면 떼어 낸 노드를 붙들고 있는 클로저가 사전 쪽에 영원히 남는다.
      stopLocaleWatch()
      clusterSize.dispose()
      // 리스너는 전부 이 서브트리 안의 요소에 걸려 있으므로 루트를 떼면 같이 사라진다.
      root.remove()
    },
  }
}
